import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { useMaxMd } from '@/hooks/useMaxMd'
import { usePageAccess } from '@/hooks/usePageAccess'
import {
  ActiveFilterPillBar,
  ColumnResizeHandle,
  FilterBuilderDrawer,
  HoverHelp,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  removeFilterPill,
  useColumnWidths,
  type FilterGroup,
  type SortDirection,
  type SortSpec,
} from '@/tableControls'
import {
  deleteFixedAsset,
  exportFixedAssetsSchedulesSheet,
  getFixedAssetMonthsSummary,
  getFixedAssetSchedule,
  listFixedAssetQboAccounts,
  listFixedAssets,
  type FixedAssetListItem,
  type MonthSummaryItem,
  type ScheduleMonthItem,
} from '@/services/fixedAssets'
import { getCategories } from '@/services/qbo'
import { useFixedAssetsSyncStore } from '@/store/fixedAssetsSyncStore'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import { downloadCsvFile, escapeCsvCell } from '@/utils/warehouseTableHelpers'
import FixedAssetDetailDrawer from './FixedAssetDetailDrawer'
import { ASSET_CLASSES } from './fixedAssetConstants'
import {
  formatFixedAssetMoney,
  formatIsoDate,
  parseMoney,
} from './fixedAssetsFormat'
import {
  FIXED_ASSETS_SCHEDULES_REGISTER_PAGE_KEY,
  PINNED_COLUMNS,
  SCHEDULES_COLUMN_KEYS,
  SCHEDULES_COLUMN_LABELS,
  SCHEDULES_DEFAULT_SORT,
  SCHEDULES_DEFAULT_WIDTHS,
  SCHEDULES_FIELD_DEFINITIONS,
  buildFixedAssetsSchedulesFilterFields,
  fixedColumnStyle,
  isSchedulesColumnSortable,
  isSchedulesPinnedColumn,
  schedulesPinnedCellClass,
  schedulesPinnedHeaderClass,
  type SchedulesColumnKey,
} from './fixedAssetsSchedulesTableConfig'
import {
  distinctFieldOptions,
  filterRowsByText,
  filterRowsWithTree,
  toggleSingleColumnSort,
} from './fixedAssetsTableFilters'
import { FixedAssetsSortableHeader } from './FixedAssetsSortableHeader'
import { buildSchedulesExportMatrix } from './fixedAssetsSchedulesExport'

type YearGroup = {
  year: number
  expenseTotal: number
  lastAccum: string
  lastNbv: string
  months: ScheduleMonthItem[]
}

function flagClass(key: string): string {
  if (key === 'cost_drift' || key === 'asset_account_missing') {
    return 'badge badge-danger'
  }
  if (key === 'fully_depreciated' || key === 'non_depreciable') {
    return 'badge badge-neutral'
  }
  return 'badge badge-warning'
}

function pctDepreciatedDisplay(value: string): number {
  return parseMoney(value)
}

function groupScheduleByYear(months: ScheduleMonthItem[]): YearGroup[] {
  const byYear = new Map<number, ScheduleMonthItem[]>()
  for (const row of months) {
    const bucket = byYear.get(row.year) ?? []
    bucket.push(row)
    byYear.set(row.year, bucket)
  }

  return [...byYear.entries()]
    .sort(([a], [b]) => a - b)
    .map(([year, yearMonths]) => {
      const sorted = [...yearMonths].sort((a, b) => a.month - b.month)
      const expenseTotal = sorted.reduce((sum, row) => sum + parseMoney(row.expense), 0)
      const last = sorted[sorted.length - 1]
      if (!last) {
        return {
          year,
          expenseTotal: 0,
          lastAccum: '0',
          lastNbv: '0',
          months: sorted,
        }
      }
      return {
        year,
        expenseTotal,
        lastAccum: last.expected_accum,
        lastNbv: last.nbv,
        months: sorted,
      }
    })
}

export default function FixedAssetsSchedules() {
  const { canEdit } = usePageAccess('fixed-assets')
  const [assets, setAssets] = useState<FixedAssetListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [scheduleMonths, setScheduleMonths] = useState<ScheduleMonthItem[]>([])
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [monthsSummary, setMonthsSummary] = useState<MonthSummaryItem[]>([])
  const [accountLabels, setAccountLabels] = useState<Record<string, string>>({})
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set())
  const [textSearch, setTextSearch] = useState('')
  const [filterTree, setFilterTree] = useState<FilterGroup | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(SCHEDULES_DEFAULT_SORT)
  const [isExporting, setIsExporting] = useState(false)
  const openCreateModal = useFixedAssetsSyncStore((state) => state.openCreateModal)
  const openEditModal = useFixedAssetsSyncStore((state) => state.openEditModal)
  const assetsRevision = useFixedAssetsSyncStore((state) => state.assetsRevision)
  const lastCreatedAssetId = useFixedAssetsSyncStore((state) => state.lastCreatedAssetId)

  const filterFields = useMemo(
    () =>
      buildFixedAssetsSchedulesFilterFields(
        distinctFieldOptions(assets.map((asset) => asset.location)),
        ASSET_CLASSES.map((assetClass) => ({ value: assetClass, label: assetClass })),
        distinctFieldOptions(assets.map((asset) => asset.method), (value) =>
          value.replace(/_/g, ' '),
        ),
      ),
    [assets],
  )

  const treeFilteredAssets = useMemo(
    () => filterRowsWithTree(assets, filterTree, filterFields),
    [assets, filterTree, filterFields],
  )

  const filteredAssets = useMemo(
    () =>
      filterRowsByText(treeFilteredAssets, textSearch, (asset) =>
        [asset.name, asset.asset_class, asset.location].filter(Boolean).join(' '),
      ),
    [treeFilteredAssets, textSearch],
  )

  const sortedAssets = useMemo(
    () => applySorts(filteredAssets, sortSpecs, SCHEDULES_FIELD_DEFINITIONS),
    [filteredAssets, sortSpecs],
  )

  const getSortDirection = useCallback(
    (field: string): SortDirection | null => {
      const spec = sortSpecs.find((entry) => entry.field === field)
      return spec?.direction ?? null
    },
    [sortSpecs],
  )

  const handleRequestSort = useCallback((field: string) => {
    setSortSpecs((prev) => toggleSingleColumnSort(prev, field))
  }, [])

  const filterPills = useMemo(
    () => generateFilterPills(filterTree, filterFields),
    [filterTree, filterFields],
  )

  const activeFilterCount = useMemo(() => countFilterConditions(filterTree), [filterTree])

  const hasActiveTextSearch = textSearch.trim().length > 0
  const showingSubset =
    activeFilterCount > 0 ||
    hasActiveTextSearch ||
    sortedAssets.length !== assets.length

  const handleRemoveFilterPill = useCallback(
    (pillId: string) => {
      setFilterTree(removeFilterPill(filterTree, pillId))
    },
    [filterTree],
  )

  const handleClearAllFilters = useCallback(() => {
    setFilterTree(null)
    setTextSearch('')
  }, [])

  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.id === selectedId) ?? null,
    [assets, selectedId],
  )

  const postedMonthKeys = useMemo(() => {
    const keys = new Set<string>()
    for (const month of monthsSummary) {
      if (month.je_status === 'posted') {
        keys.add(month.report_month)
      }
    }
    return keys
  }, [monthsSummary])

  const yearGroups = useMemo(() => groupScheduleByYear(scheduleMonths), [scheduleMonths])

  async function loadAssets() {
    setLoading(true)
    try {
      const response = await listFixedAssets()
      setAssets(response.assets)
    } catch (err) {
      console.error(err)
      toast.error(formatApiError(err, 'Failed to load assets'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAssets()
    getFixedAssetMonthsSummary()
      .then((response) => setMonthsSummary(response.months))
      .catch((err) => console.error(err))
    listFixedAssetQboAccounts()
      .then(async (accounts) => {
        const labels: Record<string, string> = {}
        for (const account of accounts) {
          labels[account.id] = account.fully_qualified_name || account.name
        }
        try {
          const categories = await getCategories()
          for (const category of categories) {
            labels[category.id] = category.name
          }
        } catch (err) {
          console.error(err)
        }
        setAccountLabels(labels)
      })
      .catch((err) => console.error(err))
  }, [])

  useEffect(() => {
    if (assetsRevision === 0) return
    void loadAssets()
    if (lastCreatedAssetId) {
      setSelectedId(lastCreatedAssetId)
    }
  }, [assetsRevision, lastCreatedAssetId])

  useEffect(() => {
    if (!selectedId) {
      setScheduleMonths([])
      return
    }

    let cancelled = false
    setScheduleLoading(true)
    getFixedAssetSchedule(selectedId)
      .then((schedule) => {
        if (!cancelled) {
          setScheduleMonths(schedule.months)
          setExpandedYears(new Set())
        }
      })
      .catch((err) => {
        console.error(err)
        if (!cancelled) toast.error(formatApiError(err, 'Failed to load schedule'))
      })
      .finally(() => {
        if (!cancelled) setScheduleLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedId, assetsRevision])

  async function handleDelete(asset: FixedAssetListItem) {
    if (
      !window.confirm(
        `Delete "${asset.name}"? This cannot be undone unless the asset has no posted depreciation history.`,
      )
    ) {
      return
    }

    try {
      await deleteFixedAsset(asset.id)
      toast.success('Asset deleted.')
      if (selectedId === asset.id) {
        setSelectedId(null)
      }
      await loadAssets()
    } catch (err) {
      console.error(err)
      toast.error(formatApiError(err, 'Failed to delete asset'))
    }
  }

  function toggleYear(year: number) {
    setExpandedYears((prev) => {
      const next = new Set(prev)
      if (next.has(year)) next.delete(year)
      else next.add(year)
      return next
    })
  }

  function accountLabel(id: string): string {
    return accountLabels[id] ?? id
  }

  function exportSchedulesCsv() {
    const { headers, rows } = buildSchedulesExportMatrix(sortedAssets)
    const csvRows = rows.map((row) => row.map(escapeCsvCell).join(','))
    downloadCsvFile('fixed-assets-schedules.csv', [headers.join(','), ...csvRows].join('\n'))
  }

  async function exportSchedulesToGoogleSheet() {
    if (sortedAssets.length === 0 || isExporting) return
    setIsExporting(true)
    try {
      const { headers, rows } = buildSchedulesExportMatrix(sortedAssets)
      const result = await exportFixedAssetsSchedulesSheet({
        headers,
        rows,
      })
      toast.success(`Created Google Sheet: ${result.name}`)
      window.open(result.url, '_blank', 'noopener,noreferrer')
    } catch (err: unknown) {
      toast.error(formatApiError(err, 'Failed to export assets to Google Sheet'))
    } finally {
      setIsExporting(false)
    }
  }

  const { getWidth, startResize, getStickyLeft } = useColumnWidths({
    pageKey: FIXED_ASSETS_SCHEDULES_REGISTER_PAGE_KEY,
    columnKeys: [...SCHEDULES_COLUMN_KEYS],
    defaults: SCHEDULES_DEFAULT_WIDTHS,
  })
  const maxMd = useMaxMd()
  const allowColumnResize = !maxMd

  const pinnedKeys = [...PINNED_COLUMNS]

  const tableWidth = useMemo(
    () => SCHEDULES_COLUMN_KEYS.reduce((sum, key) => sum + getWidth(key), 0),
    [getWidth],
  )

  const tableStyle = useMemo((): CSSProperties | undefined => {
    if (!allowColumnResize) return undefined
    return {
      width: tableWidth,
      minWidth: tableWidth,
      tableLayout: 'fixed',
    }
  }, [allowColumnResize, tableWidth])

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const isPinned = isSchedulesPinnedColumn(columnKey)
      const style: CSSProperties = {
        position: 'sticky',
        top: 0,
        zIndex: isPinned ? 20 : 10,
        backgroundColor: 'var(--table-header-bg, #f9fafb)',
      }
      if (allowColumnResize) {
        Object.assign(style, fixedColumnStyle(getWidth(columnKey)))
      }
      if (isPinned) {
        style.left = getStickyLeft(pinnedKeys, columnKey)
      }
      return style
    },
    [allowColumnResize, getStickyLeft, getWidth, pinnedKeys],
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties => {
      const isPinned = isSchedulesPinnedColumn(columnKey)
      const style: CSSProperties = allowColumnResize
        ? fixedColumnStyle(getWidth(columnKey))
        : {}
      if (isPinned) {
        return {
          ...style,
          position: 'sticky',
          left: getStickyLeft(pinnedKeys, columnKey),
          zIndex: 5,
          backgroundColor: 'var(--color-white, #fff)',
        }
      }
      return style
    },
    [allowColumnResize, getStickyLeft, getWidth, pinnedKeys],
  )

  function renderScheduleCell(
    columnKey: SchedulesColumnKey,
    asset: FixedAssetListItem,
    isSelected = false,
  ): ReactNode {
    const style = cellStyle(columnKey)
    switch (columnKey) {
      case 'name':
        return (
          <td
            key={columnKey}
            className={`font-medium text-gray-900 ${schedulesPinnedCellClass('name')} ${isSelected ? 'bg-sky-50' : ''}`}
            style={isSelected ? { ...style, backgroundColor: undefined } : style}
          >
            {asset.name}
          </td>
        )
      case 'asset_class':
        return (
          <td key={columnKey} style={style}>
            {asset.asset_class}
          </td>
        )
      case 'location':
        return (
          <td key={columnKey} style={style}>
            {asset.location}
          </td>
        )
      case 'in_service':
        return (
          <td key={columnKey} className="tabular-nums" style={style}>
            {formatIsoDate(asset.dep_start_date)}
          </td>
        )
      case 'method':
        return (
          <td key={columnKey} className="capitalize" style={style}>
            {asset.method.replace(/_/g, ' ')}
          </td>
        )
      case 'useful_life_years':
        return (
          <td key={columnKey} style={style}>
            {asset.non_depreciable ? '—' : `${asset.useful_life_years} yrs`}
          </td>
        )
      case 'purchase_price':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatFixedAssetMoney(asset.purchase_price)}
          </td>
        )
      case 'salvage_value':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatFixedAssetMoney(asset.salvage_value)}
          </td>
        )
      case 'accum_to_date':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatFixedAssetMoney(asset.accum_to_date)}
          </td>
        )
      case 'nbv':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatFixedAssetMoney(asset.nbv)}
          </td>
        )
      case 'monthly_run_rate':
        return (
          <td key={columnKey} className="tabular-nums text-right" style={style}>
            {formatFixedAssetMoney(asset.monthly_run_rate)}
          </td>
        )
      case 'pct_depreciated': {
        const pct = pctDepreciatedDisplay(asset.pct_depreciated)
        return (
          <td key={columnKey} style={style}>
            <div className="flex min-w-[5.5rem] items-center gap-2">
              <div className="h-1.5 flex-1 rounded-full bg-gray-200">
                <div
                  className="h-1.5 rounded-full bg-gray-600"
                  style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                />
              </div>
              <span className="tabular-nums text-xs text-gray-600">{pct.toFixed(0)}%</span>
            </div>
          </td>
        )
      }
      case 'flags':
        return (
          <td key={columnKey} style={style}>
            <div className="flex flex-wrap gap-1">
              {asset.flags.map((flag) => (
                <span key={flag.key} className={flagClass(flag.key)}>
                  {flag.label}
                </span>
              ))}
            </div>
          </td>
        )
      default:
        return null
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TablePageShell
        className="min-h-0 flex-1"
        rowCount={
          showingSubset ? (
            <div className="mb-2 text-xs text-gray-500 sm:text-sm">
              <span className="font-medium text-gray-700">
                {sortedAssets.length} asset{sortedAssets.length !== 1 ? 's' : ''} shown
              </span>
              <span className="ml-1">(of {assets.length.toLocaleString()} total)</span>
            </div>
          ) : null
        }
        toolbarLeft={
          <SearchInput
            value={textSearch}
            onChange={setTextSearch}
            placeholder="Search assets…"
            className="max-w-md flex-1"
          />
        }
        toolbarRight={
          <div className="flex items-center gap-2">
            <TableToolbar
              filterCount={activeFilterCount}
              sortCount={sortSpecs.length}
              showFilters={showFilters}
              showSort={showSort}
              onToggleFilters={() => setShowFilters((open) => !open)}
              onToggleSort={() => setShowSort((open) => !open)}
              exportOptions={[
                {
                  id: 'sheets',
                  label: 'Google Sheet',
                  onClick: () => void exportSchedulesToGoogleSheet(),
                  disabled: sortedAssets.length === 0,
                },
                {
                  id: 'csv',
                  label: 'CSV',
                  onClick: exportSchedulesCsv,
                  disabled: sortedAssets.length === 0,
                },
              ]}
              defaultExportId="sheets"
              isExporting={isExporting}
              exportDisabled={sortedAssets.length === 0}
            />
            {canEdit ? (
              <button type="button" className="btn btn-primary" onClick={() => openCreateModal()}>
                Add asset
              </button>
            ) : null}
          </div>
        }
        filterPills={
          <ActiveFilterPillBar
            pills={filterPills}
            onRemovePill={handleRemoveFilterPill}
            onClearAll={handleClearAllFilters}
          />
        }
      >
        <div className="table-scroll-mobile h-full min-h-0 overflow-auto overscroll-x-contain">
          <table
            className={`table-forecast border-separate border-spacing-0${allowColumnResize ? '' : ' min-w-full'}`}
            style={tableStyle}
          >
            <thead>
              <tr>
                {SCHEDULES_COLUMN_KEYS.map((columnKey) => {
                  if (columnKey === 'flags') {
                    return (
                      <th
                        key={columnKey}
                        className={`relative ${schedulesPinnedHeaderClass(columnKey)}`}
                        style={headerStyle(columnKey)}
                      >
                        <span className="inline-flex items-center pr-2">
                          {SCHEDULES_COLUMN_LABELS.flags}
                        </span>
                        {allowColumnResize ? (
                          <ColumnResizeHandle columnKey="flags" onStartResize={startResize} />
                        ) : null}
                      </th>
                    )
                  }

                  const isRightAligned =
                    columnKey === 'purchase_price' ||
                    columnKey === 'salvage_value' ||
                    columnKey === 'accum_to_date' ||
                    columnKey === 'nbv' ||
                    columnKey === 'monthly_run_rate'

                  const label =
                    columnKey === 'in_service' ? (
                      <HoverHelp label="In service" aria-label="In service date help">
                        Depreciation/amortization start date. Expense begins in this month if the day
                        is ≤ 15, otherwise the following month.
                      </HoverHelp>
                    ) : (
                      SCHEDULES_COLUMN_LABELS[columnKey]
                    )

                  return (
                    <FixedAssetsSortableHeader
                      key={columnKey}
                      label={label}
                      sortKey={columnKey}
                      sortDirection={
                        isSchedulesColumnSortable(columnKey) ? getSortDirection(columnKey) : null
                      }
                      onSort={handleRequestSort}
                      className={`${isRightAligned ? 'text-right' : ''} ${schedulesPinnedHeaderClass(columnKey)}`.trim()}
                      columnKey={columnKey}
                      style={headerStyle(columnKey)}
                      onStartResize={allowColumnResize ? startResize : undefined}
                      showResize={allowColumnResize}
                    />
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={SCHEDULES_COLUMN_KEYS.length}
                    className="py-8 text-center text-sm text-gray-500"
                  >
                    Loading assets…
                  </td>
                </tr>
              ) : sortedAssets.length === 0 ? (
                <tr>
                  <td
                    colSpan={SCHEDULES_COLUMN_KEYS.length}
                    className="py-8 text-center text-sm text-gray-500"
                  >
                    {assets.length === 0 ? (
                      'No assets in the register.'
                    ) : (
                      <>
                        No assets match your filters.{' '}
                        <button
                          type="button"
                          className="font-medium text-blue-600 hover:underline"
                          onClick={handleClearAllFilters}
                        >
                          Clear filters
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                sortedAssets.map((asset) => {
                  const isSelected = asset.id === selectedId
                  return (
                    <tr
                      key={asset.id}
                      className={`cursor-pointer ${isSelected ? 'bg-sky-50' : ''}`}
                      onClick={() => setSelectedId(asset.id)}
                    >
                      {SCHEDULES_COLUMN_KEYS.map((columnKey) =>
                        renderScheduleCell(columnKey, asset, isSelected),
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </TablePageShell>

      <FilterBuilderDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        fields={filterFields}
        root={filterTree}
        onChange={setFilterTree}
        onClearAll={() => setFilterTree(null)}
      />

      <SortBuilder
        open={showSort}
        fields={SCHEDULES_FIELD_DEFINITIONS}
        sorts={sortSpecs}
        onChange={setSortSpecs}
        onClose={() => setShowSort(false)}
        onClearAll={() => setSortSpecs(SCHEDULES_DEFAULT_SORT)}
      />

      <FixedAssetDetailDrawer
        asset={selectedAsset}
        open={selectedAsset !== null}
        onClose={() => setSelectedId(null)}
        canEdit={canEdit}
        scheduleLoading={scheduleLoading}
        scheduleMonths={scheduleMonths}
        yearGroups={yearGroups}
        expandedYears={expandedYears}
        onToggleYear={toggleYear}
        postedMonthKeys={postedMonthKeys}
        accountLabel={accountLabel}
        onEdit={openEditModal}
        onDelete={(asset) => void handleDelete(asset)}
      />
    </div>
  )
}
